import React, { useState, useEffect, createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Space } from "antd";
import ProfileService from "../../services/profile.service";
import { AuthContext } from "../../services/auth.service";
import LoadingScreen from "../../components/elements/LoadingScreen";
import PageHeader from "../../components/elements/PageHeader";
import { SideMenu } from "../../components/layouts/MainLayout/SideMenu";
import { InteractivePanel } from "../../components/layouts/MainLayout/InteractivePanel";
import { ConfirmModal } from "../../components/elements/ConfirmModal";
import { useMessage } from "../../components/elements/MessageCenter";
import { ShieldLock } from "react-bootstrap-icons";

import {
  ProfileDetails,
  CompanyDetails,
  TeamMembersDetails,
} from "./configModals";

/**
 * Saving data function
 *
 * Sending request to save profile data for user, company and billing profiles.
 *
 * @param {Object} props Parameters for the checkup
 * @param {Number} props.companyId Company ID
 * @param {String} props.firstName First Name
 * @param {String} props.lastName Last Name
 * @param {String} props.email Email
 * @param {String} props.companyName COmpany Name
 * @param {String} props.chamberOfCommerceNumber Chamber of Commerce Number
 * @param {String} props.vatNumber VAT Number
 * @param {String} props.invoiceRefNumber Invoice Reference Number
 * @param {String} props.billingFullName Billing - Full name
 * @param {String} props.billingEmail Billing - Email
 * @param {String} props.billingStreet1 Billing - Street 1
 * @param {String} props.billingStreet2 Billing - Street 2
 * @param {String} props.billingPostcode Billing - Postcode
 * @param {String} props.billingCity Billing - City
 * @param {String} props.billingCountry Billing - Country
 * @param {Function} props.modalSaveFunction Function to send data to backend
 * @param {Function} props.handlePanelClick Function to imitate clicking on panel
 * @param {Function} props.setLoadingActive Function to swtich loading page component
 * @param {Function} props.success Function to call a popup success message
 * @param {Function} props.error Function to call a popup error message
 * @param {Function} props.warning Function to call a popup warning message
 * @param {Function} props.setModalSaveStatus Function to set status for the save button inside side menu
 * @param {String} props.teamMemberEmail Team Member - Email
 *
 * @returns {null}
 */
export const SaveConfigData = async (params) => {
  const {
    companyId,
    currentConfig,
    configParams,
    modalSaveFunction,
    curModalSaveFunc,
    handlePanelClick,
    setLoadingActive,
    setCurrentConfig,
    updateConfigFields,
    success,
    error,
    setModalSaveStatus,
  } = params;

  setLoadingActive(true);

  const configObj = { ...currentConfig };

  Object.keys(configParams?.user_profile || {})?.forEach(
    (key) => (configObj["user_profile"][key] = configParams?.user_profile[key]),
  );

  configObj["user_profile"]["full_name"] = (
    configObj?.user_profile["first_name"] +
    " " +
    configObj?.user_profile["last_name"]
  ).trim();

  Object.keys(configParams?.company_profile || {})?.forEach(
    (key) =>
      (configObj["company_profile"][key] = configParams?.company_profile[key]),
  );

  Object.keys(configParams?.billing_profile || {})?.forEach(
    (key) =>
      (configObj["billing_profile"][key] = configParams?.billing_profile[key]),
  );

  const resp = await modalSaveFunction({
    companyId,
    configObj,
    curModalSaveFunc,
  });
  const respData = (await resp?.json()) || resp;

  if (respData?.result === true) {
    success("Profile data successfully saved!");
    setCurrentConfig(configObj);
    updateConfigFields(configObj);
    handlePanelClick("empty");
    setModalSaveStatus(false);
  } else if (
    respData?.error &&
    respData?.error.toLowerCase().includes("email")
  ) {
    error(respData.error);
  } else {
    error("An error occurred while saving. Please try again.");
  }

  setLoadingActive(false);
};

export const SendInvoiceMail = async (params) => {
  const {
    companyId,
    modalConfirmFunction,
    setLoadingActive,
    success,
    error,
    setIsModalVisible,
    firstName,
    lastName,
  } = params;

  setLoadingActive(true);

  // Combine first name and last name to form full name
  const fullName = `${firstName} ${lastName}`.trim();

  try {
    // Call the modal confirm function with the necessary parameters
    const resp = await modalConfirmFunction({ companyId, fullName });
    const respData = (await resp?.json()) || resp;

    // Handle the response
    if (respData?.result === true) {
      success("Invoice mail has been sent successfully!");
      setIsModalVisible(false); // Close modal after success
    } else {
      error("Failed to send invoice mail. Please try again.");
    }
  } catch (err) {
    error(
      "An error occurred while sending the invoice mail. Please try again.",
    );
  } finally {
    setLoadingActive(false);
  }
};

export const ProfileDetailsContext = createContext();
export const CompanyDetailsContext = createContext();
export const BillingDetailsContext = createContext();
export const SendInvoiceMailContext = createContext();
export const TeamMembersDetailsContext = createContext();

/**
 * Account settings page component
 *
 * @returns {ReactNode} Component with account settings content
 */

export const AccountSettings = () => {
  const navigate = useNavigate();

  // Using popup messages
  const { success, warning, error } = useMessage();

  // Using authentication context to get company info
  const { companyId, companyInfo } = useContext(AuthContext);
  const [currCompanyInfo, setCurrCompanyInfo] = useState(companyInfo);

  // Defining states for side menu
  const [sideMenuState, setSideMenuState] = useState(false);
  const [currentModal, setCurrentModal] = useState(<></>);
  const [curModalSaveFunc, setCurModalSaveFunc] = useState("");
  const [modalSaveStatus, setModalSaveStatus] = useState(false);

  // Controlling loading window status
  const [isModalVisible, setIsModalVisible] = useState(false);

  const [loadingActive, setLoadingActive] = useState(true);

  // Account properties - user, company and billing profiles
  const [currentConfig, setCurrentConfig] = useState({});
  const [configParams, setConfigParams] = useState({});

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  const [companyName, setCompanyName] = useState("");
  const [chamberOfCommerceNumber, setChamberOfCommerceNumber] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [companyActive, setCompanyActive] = useState(true);
  const [moneybirdCid, setMoneybirdCid] = useState("");
  const [billingStreet1, setBillingStreet1] = useState("");
  const [billingStreet2, setBillingStreet2] = useState("");
  const [billingPostcode, setBillingPostcode] = useState("");
  const [billingProvince, setBillingProvince] = useState("");
  const [billingCity, setBillingCity] = useState("");
  const [billingCountry, setBillingCountry] = useState("");

  const [billingFullName, setBillingFullName] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [invoiceRefNumber, setInvoiceRefNumber] = useState("");

  const [teamMembers, setTeamMembers] = useState([]); // Updated state for team members
  const [disableSaveButton, setDisableSaveButton] = useState(true);
  const updateConfigFields = (context = {}) => {
    setFirstName(context?.user_profile?.first_name || "");
    setLastName(context?.user_profile?.last_name || "");
    setEmail(context?.user_profile?.email || "");

    setCompanyName(context?.company_profile?.display_company_name || "");
    setCompanyActive(context?.company_profile?.active ?? true);
    setChamberOfCommerceNumber(
      context?.company_profile?.company_cocn_number || "",
    );
    setVatNumber(context?.company_profile?.company_vat_number || "");
    setBillingStreet1(context?.company_profile?.billing_street_address_1 || "");
    setBillingStreet2(context?.company_profile?.billing_street_address_2 || "");
    setBillingPostcode(context?.company_profile?.billing_postal_code || "");
    setBillingProvince(context?.company_profile?.billing_province || "");
    setBillingCity(context?.company_profile?.billing_city || "");
    setBillingCountry(context?.company_profile?.billing_country || "");

    setBillingFullName(context?.billing_profile?.billing_fullname || "");
    setBillingEmail(context?.billing_profile?.billing_email || "");
    setInvoiceRefNumber(
      context?.billing_profile?.company_invoice_ref_number || "",
    );

    setTeamMembers(context?.team_members || []);
  };

  // Initial page loading. Calling every time use changing the company profile
  useEffect(() => {
    (async () => {
      // Showing the loading screen while we retrieving the data from backend
      setLoadingActive(true);

      setSideMenuState(false);

      // Hiding the loading screen and aborting execution if there"s no company chosen
      if (!companyId) {
        setLoadingActive(false);
        return;
      }

      // Retrieving the data from backend
      const reqObj = { companyId };

      const response = await ProfileService.getCompanyProfileInfo(reqObj);
      const respData = await response.json();
      const context = respData?.context;

      setCurrentConfig(context);
      updateConfigFields(context);

      // Hiding the loading screen
      setLoadingActive(false);
    })();
  }, [currCompanyInfo]);

  // Update chosen company info
  useEffect(() => setCurrCompanyInfo(companyInfo), [companyInfo]);

  // Context providers for each side menu options
  const modalFunc = {
    empty: <></>,
    profile_details: (
      <ProfileDetailsContext.Provider
        value={{
          firstName,
          lastName,
          email,
          companyId,
          setModalSaveStatus,
          setConfigParams,
          navigate,
          setDisableSaveButton,
        }}
      >
        <ProfileDetails context={ProfileDetailsContext} />
      </ProfileDetailsContext.Provider>
    ),
    company_details: (
      <CompanyDetailsContext.Provider
        value={{
          companyName,
          chamberOfCommerceNumber,
          vatNumber,
          moneybirdCid,
          billingStreet1,
          billingStreet2,
          billingPostcode,
          billingProvince,
          billingCity,
          billingCountry,
          billingFullName,
          billingEmail,
          invoiceRefNumber,
          companyId,
          companyActive,
          setCompanyActive,
          setModalSaveStatus,
          setConfigParams,
          setDisableSaveButton,
        }}
      >
        <CompanyDetails context={CompanyDetailsContext} />
      </CompanyDetailsContext.Provider>
    ),
    team_members_details: (
      <TeamMembersDetailsContext.Provider
        value={{
          teamMembers,
          companyId,
        }}
      >
        <TeamMembersDetails
          context={TeamMembersDetailsContext}
          key={companyId}
        />
      </TeamMembersDetailsContext.Provider>
    ),
  };

  // Handling a click on one of panels to update the side menu data
  const handlePanelClick = (page) => {
    setSideMenuState(!sideMenuState);
    setCurrentModal(modalFunc[page]);
    setCurModalSaveFunc(page);
  };

  // Handling the modal visibility
  const handleCancel = () => {
    setIsModalVisible(false);
  };

  // Handling the modal confirmation
  const handleConfirm = async () => {
    await SendInvoiceMail({
      companyId,
      firstName,
      lastName,
      modalConfirmFunction: ProfileService.sendInvoiceMail,
      setLoadingActive,
      success,
      error,
      setIsModalVisible,
    });
  };

  // Returning page content
  return (
    <>
      {/* Initializing side menu functionality */}
      <SideMenu
        globalState={sideMenuState}
        setGlobalState={setSideMenuState}
        modalContent={currentModal}
        saveFunction={() =>
          SaveConfigData({
            companyId,
            modalSaveFunction: ProfileService.saveProfileData,
            handlePanelClick,
            setLoadingActive,
            curModalSaveFunc,
            currentConfig,
            configParams,
            setCurrentConfig,
            updateConfigFields,
            success,
            error,
            warning,
            setModalSaveStatus,
          })
        }
        saveStatus={modalSaveStatus}
        showSaveButton={curModalSaveFunc !== "team_members_details"}
        disableSaveButton={disableSaveButton}
        confirmationNeeded={true}
        customModalClass={
          curModalSaveFunc === "team_members_details"
            ? "modal-team-members"
            : ""
        }
      />
      <Space block="true">
        <LoadingScreen isActive={loadingActive} />
        <Space direction="vertical" size="large" block="true">
          <PageHeader
            categoryName="profile"
            pageName="Account & organization"
          />
          <Space direction="vertical" className="container-rows account-panel-styling" >
            <InteractivePanel
              icon={
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 26 26"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.75931 21.0583C6.41833 19.5056 7.95699 18.4167 9.74999 18.4167H16.25C18.043 18.4167 19.5816 19.5056 20.2407 21.0583M17.3333 10.2917C17.3333 12.6849 15.3932 14.625 13 14.625C10.6068 14.625 8.66666 12.6849 8.66666 10.2917C8.66666 7.89844 10.6068 5.95834 13 5.95834C15.3932 5.95834 17.3333 7.89844 17.3333 10.2917ZM23.8333 13C23.8333 18.9831 18.9831 23.8333 13 23.8333C7.01691 23.8333 2.16666 18.9831 2.16666 13C2.16666 7.01692 7.01691 2.16667 13 2.16667C18.9831 2.16667 23.8333 7.01692 23.8333 13Z"
                    stroke="currentColor"
                    strokeWidth="2.16667"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
              headerText="Profile"
              descriptionText="Your personal details and login credentials."
              available={true}
              clickFunction={() => handlePanelClick("profile_details")}
            />
            {/* Only superusers and admins get company details; the backend enforces this too. */}
            {currentConfig?.can_manage_company && (
              <InteractivePanel
                icon={
                  <svg
                    width="27"
                    height="27"
                    viewBox="0 0 27 27"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.29081 7.7381H11.3308M8.29081 12.1599H11.3308M8.29081 16.5816H11.3308M15.1998 7.7381H18.2398M15.1998 12.1599H18.2398M15.1998 16.5816H18.2398M22.1088 23.2143V6.85374C22.1088 5.61553 22.1088 4.99643 21.8679 4.52349C21.6559 4.10749 21.3177 3.76927 20.9017 3.5573C20.4287 3.31633 19.8096 3.31633 18.5714 3.31633H7.95918C6.72096 3.31633 6.10186 3.31633 5.62892 3.5573C5.21292 3.76927 4.8747 4.10749 4.66273 4.52349C4.42176 4.99643 4.42176 5.61553 4.42176 6.85374V23.2143M24.3197 23.2143H2.21088"
                      stroke="currentColor"
                      strokeWidth="2.21088"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
                headerText="Company details"
                descriptionText="Business info, tax numbers and billing details."
                available={true}
                clickFunction={() => handlePanelClick("company_details")}
              />
            )}
            <InteractivePanel
              icon={
                <svg
                  width="27"
                  height="27"
                  viewBox="0 0 27 27"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21.1222 18.5838C22.8306 19.4421 24.2953 20.8193 25.3645 22.5416C25.5762 22.8826 25.682 23.0532 25.7187 23.2893C25.793 23.7692 25.4649 24.3592 25.0179 24.5491C24.798 24.6425 24.5506 24.6425 24.0558 24.6425M18.7752 13.5325C20.514 12.6685 21.7089 10.8742 21.7089 8.8009C21.7089 6.72757 20.514 4.93333 18.7752 4.06925M16.4283 8.8009C16.4283 11.7173 14.0642 14.0814 11.1478 14.0814C8.23144 14.0814 5.86727 11.7173 5.86727 8.8009C5.86727 5.88454 8.23144 3.52036 11.1478 3.52036C14.0642 3.52036 16.4283 5.88454 16.4283 8.8009ZM3.00314 22.2233C4.87398 19.4143 7.82619 17.6018 11.1478 17.6018C14.4694 17.6018 17.4216 19.4143 19.2925 22.2233C19.7023 22.8386 19.9072 23.1463 19.8837 23.5394C19.8653 23.8454 19.6647 24.2201 19.4201 24.405C19.1061 24.6425 18.6741 24.6425 17.8102 24.6425H4.48536C3.62148 24.6425 3.18954 24.6425 2.87548 24.405C2.63094 24.2201 2.43033 23.8454 2.41196 23.5394C2.38836 23.1463 2.59329 22.8386 3.00314 22.2233Z"
                    stroke="currentColor"
                    strokeWidth="2.04082"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
              headerText="Team members"
              descriptionText="Invite and manage users."
              available={true}
              clickFunction={() => handlePanelClick("team_members_details")}
            />
            {/* <InteractivePanel
                            icon={<ShieldLock size={21} />}
                            headerText="Send invoice mail"
                            descriptionText="Send an invoice mail linked to your Code-Cube.io account"
                            available={true}
                            clickFunction={() => setIsModalVisible(true)} // Open modal on click
                        /> */}
          </Space>
        </Space>
      </Space>
      <ConfirmModal
        visible={isModalVisible}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        title="Send Invoice Mail"
        content="Are you sure you want to send the invoice mail?"x
        okText="Send"
        cancelText="Cancel"
      />
    </>
  );
};
